import { Maybe } from '@codegrade/maybe';
import { sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/**
 * A function that returns a Default state.
 *
 * @returns Default state of this store module.
 */
const makeInitialState = () => ({
    allTenants: { pagination: { tag: 'not-started' } },
    singleTenants: {},
    statistics: {},
    loaders: {
        singleTenant: {},
        statistics: {},
    },
});
const moduleBuilder = storeBuilder.module('tenants', makeInitialState());
/** The Tenant store module. */
export var TenantsStore;
(function (TenantsStore) {
    /**
     * Get the tenant with the given id if we have retrieved it.
     *
     * @param state The current state of the TenantsStore.
     * @returns A function to retrieve a tenant by id.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getTenant(state) {
        const lookup = new Map(state.allTenants.data?.map(tenant => [tenant.id, tenant]));
        return (tenantId) => Maybe.fromNullable(state.singleTenants[tenantId]).altLazy(() => Maybe.fromNullable(lookup.get(tenantId)));
    }
    /**
     * Get the tenant with the given id if we have retrieved it.
     *
     * @param state The current state of the TenantsStore.
     * @returns A function to retrieve a tenant by id.
     */
    TenantsStore.getTenant = moduleBuilder.read(_getTenant, 'getTenant');
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getStats(state) {
        return (tenant) => Maybe.fromNullable(state.statistics[tenant.id]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.getStatistics = moduleBuilder.read(_getStats, 'getStatistics');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.allTenants = moduleBuilder.read(state => utils.getPaginatedState(state.allTenants), 'allTenants');
    const commitTenants = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { tenants }) => {
        state.allTenants = tenants;
    }, 'commitTenantsLoader');
    const commitSingleTenantLoader = moduleBuilder.commit(
    /**
     * Commit the loader for a single tenant.
     *
     * @param state The current state of the TenantsStore.
     * @param payload The id of the single tenant we are loading, and the loader.
     */
    (state, { id, loader, }) => {
        if (loader.isJust()) {
            utils.vueSet(state.loaders.singleTenant, id, loader.value);
        }
        else {
            utils.vueDelete(state.loaders.singleTenant, id);
        }
    }, 'commitSingleTenantLoader');
    const commitStatsLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { tenantId, loader }) => {
        if (loader.isJust()) {
            utils.vueSet(state.loaders.statistics, tenantId, loader.value);
        }
        else {
            utils.vueDelete(state.loaders.statistics, tenantId);
        }
    }, 'commitStatsLoader');
    /**
     * Commit a single tenant into the store.
     *
     * @param state The current state of the TenantsStore.
     * @param payload The tenant that we want to commit into the store.
     */
    TenantsStore.commitSingleTenant = moduleBuilder.commit((state, { tenant, }) => {
        utils.vueSet(state.singleTenants, tenant.id, tenant);
        let didAdd = false;
        let newData = (state.allTenants.data ?? []).map(t => {
            if (t.id === tenant.id) {
                didAdd = true;
                return tenant;
            }
            return t;
        });
        if (!didAdd && state.allTenants.pagination.tag === 'finished') {
            newData.push(tenant);
            newData = sort.sortBy(newData, t => [t.orderCategory, t.name, t.id], {
                reversePerKey: [true, false, false],
            });
        }
        utils.vueSet(state, 'allTenants', {
            ...state.allTenants,
            data: newData,
        });
    }, 'commitSingleTenant');
    const commitStats = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueSet(state.statistics, payload.tenantId, payload.stats);
    }, 'commitStats');
    /**
     * Method to load all tenants into the store. It is done without the statistics.
     * Those are loaded separately per tenant once it's been opened to avoid unnecessary slowdown.
     */
    TenantsStore.loadTenants = moduleBuilder.dispatch(async (ctx, { toLoad, pageSize, }) => {
        await utils.loadPaginatedWithForce({
            commitData: tenants => commitTenants({ tenants }),
            getData: () => ctx.state.allTenants,
            load: async (nextToken) => await client.p.tenant.getAll({ nextToken, pageSize }),
            toLoad,
        });
    }, 'loadTenants');
    /** Load a single tenant, used for targeted loading, when we do not want to list tenants. */
    TenantsStore.loadSingleTenant = moduleBuilder.dispatch(async (ctx, { tenantId, force = false, fullTenantNeeded, }) => {
        await utils.storeLoadWithMaybeForce({
            value: fullTenantNeeded
                ? Maybe.fromNullable(ctx.state.singleTenants[tenantId]).filter(tenant => tenant instanceof models.Tenant)
                : TenantsStore.getTenant()(tenantId),
            addLoader: loader => commitSingleTenantLoader({ id: tenantId, loader: Maybe.of(loader) }),
            force,
            load: async () => {
                const { body } = await client.p.tenant.get({ tenantId });
                TenantsStore.commitSingleTenant({ tenant: new models.Tenant(body) });
            },
            loader: Maybe.fromNullable(ctx.state.loaders.singleTenant[tenantId]),
            removeLoader: () => commitSingleTenantLoader({ id: tenantId, loader: Maybe.empty() }),
        });
    }, 'loadSingleTenant');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.loadTenantStatistics = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (ctx, { tenantId, force = false }) => {
        utils.storeLoadWithMaybeForce({
            value: Maybe.fromNullable(ctx.state.statistics[tenantId]),
            loader: Maybe.fromNullable(ctx.state.loaders.statistics[tenantId]),
            addLoader: loader => commitStatsLoader({ loader: Maybe.of(loader), tenantId }),
            force,
            load: async () => {
                const { body } = await client.p.tenant.getStats({ tenantId });
                const stats = models.TenantStatistics.fromServerData(body);
                commitStats({ tenantId, stats });
            },
            removeLoader: () => commitStatsLoader({ loader: Maybe.empty(), tenantId }),
        });
    }, 'loadTenantStatistics');
    /** Action to patch data of a tenant. */
    TenantsStore.patchTenant = moduleBuilder.dispatch(async (_, { tenant, data }) => {
        const res = await client.p.tenant.patch({
            tenantId: tenant.id,
            requestBody: data,
        });
        return res.addCallbacks({
            /** Adding the call to commit the data to the store. */
            onAfterSuccess() {
                TenantsStore.commitSingleTenant({ tenant: new models.Tenant(this.body) });
            },
        });
    }, 'patchTenant');
    /** Action to create a new tenant. */
    TenantsStore.createTenant = moduleBuilder.dispatch(async (_, { data }) => {
        const res = await client.p.tenant.create({ requestBody: data });
        const tenant = new models.Tenant(res.body);
        TenantsStore.commitSingleTenant({ tenant });
        return res;
    }, 'createTenant');
})(TenantsStore || (TenantsStore = {}));
